import React, { useState, useEffect, SyntheticEvent, useCallback } from 'react';

import { 
  FeatureSource,
  ModelBase, 
  ReusableString
} from "@models";

import { 
  CharacterFeatureSource, 
  CharacterSheetInterface, 
  Creature, 
  CreatureInstance, 
  SummonInstance 
} from "@dndModels";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import LoadingBox from "@components/input/LoadingBox";
import DisplayString from "@components/input/strings/DisplayString";
import CommonUtilities from "@utilities/common_utilities";
import { DataType, isDataType } from '@dndModels/Types';

// ─── Segment types ────────────────────────────────────────────────────────────

type TextSegment = { kind: "text"; value: string };
type ReplacerSegment = { kind: "replacer"; data_type: string; address: string[] };
type ModalSegment = { kind: "modal"; data_type: string; address: string[]; name: string };
type LinkSegment = { kind: "link"; data_type: string; address: string[]; name: string };

type Segment = TextSegment | ReplacerSegment | ModalSegment | LinkSegment;

// ─── Parse a string into segments (synchronous) ───────────────────────────────

function parseSegments(input: string): Segment[] {
  const segments: Segment[] = [];

  while (input.length > 0) {
    // Find the earliest special token
    const replacerIdx = input.indexOf("[Replacer:");

    const candidates = [replacerIdx].filter(i => i >= 0);
    if (candidates.length === 0) {
      segments.push({ kind: "text", value: input });
      break;
    }

    const first = Math.min(...candidates);

    // Push any leading text
    if (first > 0) {
      segments.push({ kind: "text", value: input.substring(0, first) });
      input = input.substring(first);
      continue;
    }

    if (input.startsWith("[Replacer:")) {
      input = input.substring(10);
      const end = input.indexOf("]");
      if (end < 0) { segments.push({ kind: "text", value: input }); break; }
      const stuff = input.substring(0, end);
      input = input.substring(end + 1);

      const semi = stuff.indexOf(";");
      const data_type = stuff.substring(0, semi);
      const parenOpen = stuff.indexOf("(");
      const parenClose = stuff.indexOf(")");
      const address = stuff.substring(parenOpen + 1, parenClose).split(",");
      segments.push({ kind: "replacer", data_type, address });

    }
  }

  return segments;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  display_string: string;
  cfs?: CharacterSheetInterface | CreatureInstance | SummonInstance | CharacterFeatureSource | Creature | FeatureSource;
  log_it?: boolean;
  descWidth?: number;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

// ─── Sub-component: renders one Replacer segment asynchronously ───────────────

interface ReplacerNodeProps {
  data_type: string;
  address: string[];
  cfs: Props["cfs"];
  log_it: boolean;
}

function ReplacerNode({ data_type, address, cfs, log_it }: ReplacerNodeProps) {
  const [content, setContent] = useState<React.ReactNode>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (api.smart_hash[data_type]) {
        const obj = api.smart_hash[data_type].find(o => address.includes(o._id));
        if (obj instanceof ReusableString) {
          const node = (
            <div>
              <div className="header">{obj.label}</div>
              <div>&nbsp;</div>
              {/* Recursively render the content string */}
              <DisplayStringWithReplacements display_string={obj.content} cfs={cfs} log_it={log_it} />
              <div>&nbsp;</div>
            </div>
          );
          if (!cancelled) { setContent(node); setLoading(false); }
        } else if (obj instanceof ModelBase) {
          const node = (
            <div>
              <div className="header">{obj.use_name}</div>
              <div>&nbsp;</div>
              <DisplayStringWithReplacements display_string={obj.description} cfs={cfs} log_it={log_it} />
              <div>&nbsp;</div>
            </div>
          );
          if (!cancelled) { setContent(node); setLoading(false); }
        } else {
          if (!cancelled) { setLoading(false); }
        }
      } else {
        if (!cancelled) { setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [data_type, address, cfs, log_it]);

  if (loading) return <LoadingBox />;
  return <>{content}</>;
}

// ─── Sub-component: renders one text segment asynchronously ───────────────────

interface TextNodeProps {
  value: string;
  cfs: Props["cfs"];
}

function TextNode({ value, cfs }: TextNodeProps) {
  const [resolved, setResolved] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await char_util.getStringWithReplacementsWithObjectAsync({ clean_me: value, obj: cfs });
      if (!cancelled) setResolved(result);
    })();
    return () => { cancelled = true; };
  }, [value, cfs]);

  if (resolved === null) return <LoadingBox />;
  return <DisplayString str={resolved} />;
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function DisplayStringWithReplacements({
  display_string,
  cfs,
  log_it = false,
  descWidth
}: Props) {
  // Parse segments synchronously so the skeleton renders on the first paint.
  // useMemo recalculates only when display_string changes.
  const segments = React.useMemo(
    () => descWidth ? null : parseSegments(display_string),
    [display_string, descWidth]
  );

  const [descStrings, setDescStrings] = useState<string[] | null>(null);

  // For descWidth mode we still need an async load.
  useEffect(() => {
    if (!descWidth) return;
    setDescStrings(null);
    let cancelled = false;
    (async () => {
      const strs = await char_util.getDisplayStringsAsync(display_string, cfs);
      if (!cancelled) setDescStrings(strs);
    })();
    return () => { cancelled = true; };
  }, [display_string, cfs, descWidth]);

  // Kick off any required list_of_type prefetches in the background.
  // This does NOT block rendering — ReplacerNode/TextNode will re-render
  // naturally once smart_hash is populated and they retry.
  useEffect(() => {
    if (!display_string.includes("{list_of_type")) return;
    let data_types = common_util.extractListTypes(display_string);
    const smart_keys = Object.keys(api.smart_hash);
    const temp_keys  = Object.keys(api.temp_hash);
    data_types = common_util.unique_array(
      data_types.filter(o => !smart_keys.includes(o) && !temp_keys.includes(o))
    );
    if (data_types.length > 0) {
      api.getSetOfObjects({ data_types }); // fire-and-forget
    }
  }, [display_string]);

  const linkClicked = (data_type: DataType, address_str: string) => {
    // TODO: open a modal or navigate
  };

  if (descWidth && !descStrings) return <LoadingBox />;

  if (descWidth && descStrings) {
    return (
      <div style={{ width: `${descWidth}px`, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {descStrings.join()}
      </div>
    );
  }

  if (!segments) return null;

  return (
    <div className="padded">
      {segments.map((seg, i) => {
        switch (seg.kind) {

          case "replacer":
            return (
              <ReplacerNode
                key={i}
                data_type={seg.data_type}
                address={seg.address}
                cfs={cfs}
                log_it={log_it}
              />
            );

          case "modal": {
            const address_str = seg.address.join(",");
            if (!isDataType(seg.data_type)) return null;
            const obj = api.smart_hash[seg.data_type]?.find(o => seg.address.includes(o._id));
            return obj instanceof ModelBase ? (
              <a key={i} className="modal_link"
                href={`/dandd/${seg.data_type}/edit/${address_str}`}
                target="_blank" rel="noopener noreferrer"
                onClick={(e: SyntheticEvent) => { linkClicked(seg.data_type as DataType, address_str); e.preventDefault(); }}>
                {seg.name}
              </a>
            ) : (
              <span key={i}>VALUE NOT FOUND!</span>
            );
          }

          case "link": {
            const address_str = seg.address.join(",");
            const obj = api.smart_hash[seg.data_type]?.find(o => seg.address.includes(o._id));
            return obj instanceof ModelBase ? (
              <a key={i} className="new_tab_link"
                href={`/dandd/${seg.data_type}/edit/${address_str}`}>
                {seg.name}
              </a>
            ) : (
              <span key={i}>VALUE NOT FOUND!</span>
            );
          }

          case "text":
            return <TextNode key={i} value={seg.value} cfs={cfs} />;

          default:
            return null;
        }
      })}
    </div>
  );
}